import React, { useState, useEffect } from 'react';
import { Card } from '../../components/base/Card';
import { Button } from '../../components/base/Button';
import { SEO, getOrganizationSchema } from '../../utils/seo';
import { supabase } from '../../lib/supabase';

export const ContactPage: React.FC = () => {
  const [pageData, setPageData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .eq('slug', 'iletisim')
        .eq('is_published', true)
        .single();

      if (error) throw error;
      if (data) {
        setPageData(data);
      }
    } catch (error) {
      console.error('Sayfa yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#C1121F]"></div>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic here
    console.log('Form submitted:', formData);
  };

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: 'Adres',
      details: ['1459. Cad, İvedik OSB', 'Özanadolu Sanayi Sitesi No:3', '06480 Yenimahalle/Ankara'],
      action: 'Haritada Gör'
    },
    {
      icon: 'ri-phone-line',
      title: 'Telefon',
      details: ['0312 394 56 35'],
      action: 'Hemen Ara'
    },
    {
      icon: 'ri-mail-line',
      title: 'E-posta',
      details: ['info@3dpmakina.com'],
      action: 'E-posta Gönder'
    },
    {
      icon: 'ri-time-line',
      title: 'Çalışma Saatleri',
      details: ['Pazartesi - Cuma: 08:00 - 18:00', 'Cumartesi: 08:00 - 14:00', 'Pazar: Kapalı'],
      action: null
    }
  ];

  const departments = [
    {
      name: 'Genel Bilgi',
      email: 'info@3dpmakina.com',
      phone: '0312 394 56 35',
      description: 'Genel sorular ve bilgi almak için'
    },
    {
      name: 'Teklif & Satış',
      email: 'info@3dpmakina.com',
      phone: '0312 394 56 35',
      description: 'Proje teklifleri ve satış danışmanlığı'
    },
    {
      name: 'Teknik Destek',
      email: 'info@3dpmakina.com',
      phone: '0312 394 56 35',
      description: 'Teknik sorular ve mühendislik desteği'
    },
    {
      name: 'Kalite Kontrol',
      email: 'info@3dpmakina.com',
      phone: '0312 394 56 35',
      description: 'Kalite raporları ve sertifikalar'
    }
  ];

  const contactSchema = {
    "@context": "https://schema.org",
    "@graph": [
      getOrganizationSchema(),
      {
        "@type": "ContactPage",
        "name": "İletişim - 3DP Makina",
        "description": "Projeleriniz için profesyonel destek almaya hazır mısınız? Uzman ekibimiz size yardımcı olmak için burada.",
        "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/iletisim`
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="İletişim | 3DP Makina - Bize Ulaşın"
        description="Projeleriniz için profesyonel destek almaya hazır mısınız? Uzman ekibimiz size yardımcı olmak için burada. Hemen iletişime geçin."
        keywords="iletişim, 3DP Makina, telefon, adres, e-posta, teklif, destek"
        canonical={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/iletisim`}
        ogTitle="İletişim | 3DP Makina - Bize Ulaşın"
        ogDescription="Projeleriniz için profesyonel destek almaya hazır mısınız? Uzman ekibimiz size yardımcı olmak için burada."
        ogUrl={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/iletisim`}
        jsonLd={contactSchema}
      />

      {/* Hero Section */}
      <section 
        className="relative h-96 bg-cover bg-center bg-no-repeat flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(22, 22, 22, 0.7), rgba(22, 22, 22, 0.7)), url('https://public.readdy.ai/ai/img_res/6c5bfe551ffb42fe5e5ed5d3eec8cfe5.jpg')`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <h1 className="text-5xl font-bold mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              İletişim
            </h1>
            <p className="text-xl leading-relaxed">
              Projeleriniz için profesyonel destek almaya hazır mısınız? 
              Uzman ekibimiz size yardımcı olmak için burada.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <Card key={index} hover className="text-center">
                <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${info.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-[#161616] mb-3">
                  {info.title}
                </h3>
                <div className="space-y-1 mb-4">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600 text-sm">
                      {detail}
                    </p>
                  ))}
                </div>
                {info.action && (
                  <Button variant="outline" size="sm" className="border-[#C1121F] text-[#C1121F] hover:bg-[#C1121F] hover:text-white">
                    {info.action}
                  </Button>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#161616] mb-8" style={{ fontFamily: '"Poppins", sans-serif' }}>
                Bize Ulaşın
              </h2>
              <Card>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ad Soyad *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                        placeholder="Adınız ve soyadınız"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        E-posta *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                        placeholder="ornek@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefon
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                        placeholder="+90 5XX XXX XX XX"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Firma
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                        placeholder="Firma adınız"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Konu *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                    >
                      <option value="">Konu seçiniz</option>
                      <option value="teklif">Teklif Talebi</option>
                      <option value="teknik">Teknik Destek</option>
                      <option value="kalite">Kalite & Sertifikalar</option>
                      <option value="genel">Genel Bilgi</option>
                      <option value="diğer">Diğer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mesaj *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent resize-none"
                      placeholder="Mesajınızı buraya yazın... (Maksimum 500 karakter)"
                    />
                    <div className="text-right text-sm text-gray-500 mt-1">
                      {formData.message.length}/500
                    </div>
                  </div>

                  <Button type="submit" variant="primary" size="lg" className="w-full">
                    <i className="ri-send-plane-line mr-2"></i>
                    Mesaj Gönder
                  </Button>
                </form>
              </Card>
            </div>

            {/* Departments */}
            <div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#C1121F] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#161616] mb-2">Adres</h3>
                  <p className="text-gray-600">
                    1459. Cad, İvedik OSB<br />
                    Özanadolu Sanayi Sitesi No:3<br />
                    06480 Yenimahalle/Ankara
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 mt-6">
                <div className="w-12 h-12 bg-[#C1121F] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#161616] mb-2">Telefon</h3>
                  <p className="text-gray-600">0312 394 56 35</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 mt-6">
                <div className="w-12 h-12 bg-[#C1121F] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#161616] mb-2">E-posta</h3>
                  <p className="text-gray-600">info@3dpmakina.com</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#161616] mb-4" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Konumumuz
            </h2>
            <p className="text-xl text-gray-600">
              İvedik Organize Sanayi Bölgesi'nde modern tesisimizde hizmet veriyoruz.
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3058.5!2d32.7!3d39.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDU0JzAwLjAiTiAzMsKwNDInMDAuMCJF!5e0!3m2!1str!2str!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="3DP Makina Konum"
            ></iframe>
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="py-20 bg-[#C1121F]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Hızlı İletişim İçin WhatsApp
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Acil sorularınız için WhatsApp üzerinden 7/24 ulaşabilirsiniz.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="secondary" 
              size="lg"
              onClick={() => window.open('tel:03123945635', '_self')}
            >
              <i className="ri-phone-line mr-2"></i>
              0312 394 56 35
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-white text-white hover:bg-white hover:text-[#C1121F]"
              onClick={() => window.open('https://wa.me/903123945635', '_blank')}
            >
              <i className="ri-whatsapp-line mr-2"></i>
              WhatsApp ile Yaz
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
